import { Delete } from '@mui/icons-material'
import { CartProduct, IBundleProduct, useCartProduct } from 'cart'
import { I18n, MY_PLACE_SELLER, Translate2 as Translate } from 'common'
import { show } from '@ebay/nice-modal-react'
import { ProductLocationModel, ProductModel } from 'interfaces'
import { removeProductThunk } from 'cart'
import { observer } from 'mobx-react-lite'
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react'
import { Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { BundleTooltip } from 'results'
import { RootState, store } from 'store'
import styled from 'styled-components'
import { Config } from '../../../config/ConfigManager'
import { ISeller } from '../../../services/MultiSellerServicesProvider'
import { StoreInstances } from '../../../store/StoreInstancesContainer'
import { Vehicle } from '../../../store/models/Vehicles'
import { useUiStateStore } from '../../../store/uiState/UiStateStore'
import { useUserStore } from '../../../store/user/UserStore'
import { defaultTheme } from '../../../theme/index'
import { Price } from '../../../ui-components/products/Price'
import { QuantitySelector } from '../../../ui-components/products/QuantitySelector'
import { BundledTag } from './BundledTag'
import { CartPartDetailsDrawer } from './CartPartDetailsDrawer'
import LocationWarning from './LocationWarning'
import { useSelector } from 'react-redux'
import { getMinQtyAtLocation } from 'libs/cart/src/store/cartSelectors'
import { useQuantitySelectorToolbox } from 'src/ui-components/products/useQuantitySelectorToolbox'

const getTotalQuantityAvailableOnLocations = (
  product: ProductModel
): number => {
  const locations = product?.location || []
  return locations.reduce((acc, location) => {
    const qtyAvailable = location?.qtyAvailable
    const total = acc + qtyAvailable
    return total
  }, 0)
}

interface CartItemTableProps {
  product: CartProduct
  vehicle?: Vehicle
  location?: ProductLocationModel
  updateQuantity: (qty: number) => Promise<void>
  currentQty: number
  showLocationSelector: () => void
  priceSpinner: boolean
  initialSelectorQty: number
  yourCostAfterPriceBreaks: () => number
  sellerDetails?: ISeller
  bundledProduct: IBundleProduct
  isMobileView?: boolean
  setInputValueRef: React.MutableRefObject<(val: string) => void>
}

export const CartItemTable = observer(
  (props: CartItemTableProps): ReactElement => {
    const {
      product,
      vehicle,
      location,
      yourCostAfterPriceBreaks,
      currentQty,
      priceSpinner,
      showLocationSelector,
      updateQuantity,
      sellerDetails,
      bundledProduct,
      isMobileView = false,
      setInputValueRef,
    } = props

    const navigate = useNavigate()
    const uiStore = useUiStateStore()

    const isCartNormalMode = Config?.isNotCartOnlyMode

    const userStore = useUserStore()
    const orderIfNotAvailable =
      userStore?.preferences?.findit_orderIfNotAvail === 'true'

    const cartProduct = useCartProduct({
      product,
      vehicle,
      sellerDetails,
      locationId: location.locationId,
    })

    const { onInputTooltipMsg, shouldShowLocationSelector } =
      useQuantitySelectorToolbox(cartProduct, location)

    const [inputValue, setInputValue] = useState((currentQty || 0).toString())

    useEffect(() => {
      setInputValueRef.current = setInputValue
    }, [setInputValueRef])

    useEffect(() => {
      setInputValue((currentQty || 0).toString())
    }, [currentQty])

    const updateQuantityRef = useRef(updateQuantity)
    updateQuantityRef.current = updateQuantity

    const handleQtyChange = useCallback((newQty: number): Promise<void> => {
      return updateQuantityRef.current(newQty)
    }, [])

    const minQtyAtLocationCart = useSelector((state: RootState) =>
      getMinQtyAtLocation(state, vehicle, MY_PLACE_SELLER, location, product)
    )

    const minQtyAtLocation = useMemo(() => {
      return location.minQty ?? minQtyAtLocationCart
    }, [location.minQty, minQtyAtLocationCart])

    const totalQuantityAvailableOnLocations =
      getTotalQuantityAvailableOnLocations(product)
    const showChangeLocation =
      totalQuantityAvailableOnLocations !== 0 || isCartNormalMode

    const navigateToProductDetails = () => {
      const encodedVehicle = encodeURIComponent(JSON.stringify(vehicle))

      const multiSellerParams = sellerDetails.coverageKey
        ? `&coverageKey=${encodeURIComponent(sellerDetails.coverageKey)}`
        : ''

      navigate(
        `/product/?partNumber=${encodeURIComponent(product.partNumber)}&lineCode=${encodeURIComponent(product.lineCode)}${multiSellerParams}&vehicle=${encodedVehicle}`,
        { state: { ...product, from: 'cart' } }
      )
    }

    const coreCost = location.coreCost ?? 0
    const total = (yourCostAfterPriceBreaks() + coreCost) * currentQty

    const validatePrimaryLocation =
      StoreInstances.userStore.preferences.cart_nonPrimaryLocWarningEnabled?.toLowerCase() ===
      'true'

    const handleDelete = (): void => {
      store.dispatch(
        removeProductThunk({
          product,
          quantity: currentQty,
          locationId: location.locationId.toString(),
          vehicle,
          seller: sellerDetails,
          trackCartDelete: true,
        })
      )
    }

    const isPrimary = location?.locType === 'primary'

    const yourCost = yourCostAfterPriceBreaks()

    const renderYourCost = () => {
      if (priceSpinner) return <PriceSpinner />

      if (bundledProduct.bundled) {
        return (
          <>
            <Price num={bundledProduct.bundlePrice} />
            <PreviousPrice>
              <Price num={yourCost} />
            </PreviousPrice>
          </>
        )
      }

      return <Price num={yourCost} />
    }

    const renderTotal = () => {
      if (!bundledProduct.bundled) return <Price num={total} />

      const bundleTotal =
        (bundledProduct.bundlePrice + bundledProduct.unitCorePrice) *
        bundledProduct.quantityRequested

      return (
        <>
          <Price num={bundleTotal} />
          <PreviousPrice>
            <Price num={total} />
          </PreviousPrice>
        </>
      )
    }

    const renderProductInfo = () => (
      <Cell>
        {isCartNormalMode ? (
          <ProductNameLink
            onClick={navigateToProductDetails}
            $isMobile={isMobileView}
          >
            {product.description}
          </ProductNameLink>
        ) : (
          <ProductName $isMobile={isMobileView}>
            {product.description}
          </ProductName>
        )}
        <div className="text-nowrap">
          <NormalText>
            <I18n name="partNum" />
            :&nbsp;
          </NormalText>
          {product.partNumber}
        </div>
        {product?.lineCode && (
          <div className="text-nowrap">
            <NormalText>
              <I18n name="lineCode" />
              :&nbsp;
            </NormalText>
            {product.lineCode}
          </div>
        )}
      </Cell>
    )

    const renderAvailability = () => (
      <Cell>
        {location.qtyAvailable} {Translate('at')} {location.called}
        {showChangeLocation && (
          <>
            <LocalLink onClick={showLocationSelector}>
              {StoreInstances?.uiStore?.isMultiLocationEnabled() ? (
                <I18n name="moreLocations" />
              ) : (
                <I18n name="changeLocation" />
              )}
            </LocalLink>
            <LocationWarning
              product={product}
              activeLocation={location}
              validatePrimaryLocation={validatePrimaryLocation}
            />
          </>
        )}
      </Cell>
    )

    const renderQuantity = () => (
      <Cell className="d-flex flex-row">
        {isCartNormalMode ? (
          <QuantitySelector
            orderIfNotAvailable={orderIfNotAvailable && isPrimary}
            onChange={handleQtyChange}
            minQty={Math.max(minQtyAtLocation, location?.buyIncrement || 0)}
            onClick={shouldShowLocationSelector}
            stepSize={location.buyIncrement}
            onInputHoverTooltipMsg={onInputTooltipMsg}
            orderNumber={product?.orderNumber}
            value={inputValue}
            setValue={setInputValue}
            isLoading={priceSpinner}
          />
        ) : (
          <p>{currentQty}</p>
        )}
      </Cell>
    )

    const renderDetailsLink = () => (
      <LocalLink
        onClick={() => {
          show(CartPartDetailsDrawer, {
            shoppingCartProduct: product,
            yourCost: yourCostAfterPriceBreaks(),
            selectedLocation: location,
            total,
          })
        }}
      >
        <I18n name="details" />
      </LocalLink>
    )

    if (isMobileView) {
      return (
        <MobileLayout>
          <MobileSection>{renderProductInfo()}</MobileSection>
          <MobileSection>{renderAvailability()}</MobileSection>
          <MobileSection>{renderQuantity()}</MobileSection>
          <MobileTotalRow>
            {renderTotal()}
            {renderDetailsLink()}
          </MobileTotalRow>
        </MobileLayout>
      )
    }

    return (
      <>
        <Td className="td-column">{renderProductInfo()}</Td>
        {uiStore.showYourCost() && (
          <Td className="hide-cell">
            <Cell>
              {renderYourCost()}

              {location.coreCost > 0 && (
                <NormalText>
                  <I18n name="coreFee" />
                  &nbsp;
                  <Price num={location.coreCost} />
                </NormalText>
              )}
              {uiStore?.showBundles() &&
                (bundledProduct.bundled ? (
                  <BundledTag />
                ) : (
                  product?.eligibleBundles?.length > 0 && (
                    <BundleTooltip bundles={product?.eligibleBundles} />
                  )
                ))}
            </Cell>
          </Td>
        )}
        <Td className="hide-cell">
          <Cell className="text-nowrap">
            <Price num={location.list} />
            {location.coreList > 0 && (
              <NormalText>
                <I18n name="coreList" />
                &nbsp;
                <Price num={location.coreList} />
              </NormalText>
            )}
          </Cell>
        </Td>
        <Td className="td-column">{renderAvailability()}</Td>
        <Td className="td-column">{renderQuantity()}</Td>
        <Td>
          <Cell>
            {renderTotal()}
            <LocalLink
              onClick={() => {
                show(CartPartDetailsDrawer, {
                  shoppingCartProduct: product,
                  yourCost: yourCostAfterPriceBreaks(),
                  selectedLocation: location,
                  total,
                })
              }}
              className="text-visibility"
            >
              <I18n name="details" />
            </LocalLink>
          </Cell>
        </Td>
        {isCartNormalMode && (
          <Td className="hide-cell">
            <Cell>
              <Delete
                sx={{
                  cursor: 'pointer',
                }}
                onClick={handleDelete}
                htmlColor={defaultTheme.colors.red}
              />
            </Cell>
          </Td>
        )}
      </>
    )
  }
)

const Td = styled.td`
  vertical-align: top;
  padding: 20px 14px;
  width: 14%;
`

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  font-size: 16px;
  line-height: 24px;
  text-align: start;
  font-weight: bold;
`

const MobileLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  gap: 8px;
`

const MobileSection = styled.div`
  width: 100%;
  min-width: 0;
`

const MobileTotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
`

interface ProductNameProps {
  $isMobile?: boolean
}

const ProductNameLink = styled.div<ProductNameProps>`
  max-width: ${(p) => (p.$isMobile ? '100%' : '250px')};
  text-decoration: underline;
  font-size: 18px;
  cursor: pointer;
  overflow-wrap: anywhere;
  word-break: break-word;
`

const ProductName = styled(ProductNameLink)`
  text-decoration: none;
  cursor: auto;
`

const NormalText = styled.span`
  font-weight: normal;
  font-size: 16px;
`

interface ILocalLink {
  cursonType?: string
}

const LocalLink = styled.div<ILocalLink>`
  color: ${defaultTheme.colors.blueLight};
  text-decoration: underline;
  cursor: ${(p) => p.cursonType ?? 'pointer'};
`

const PreviousPrice = styled.div`
  text-decoration: line-through;
  font-weight: normal;
`

const PriceSpinner = styled(Spinner)`
  height: 20px;
  width: 20px;
  color: ${defaultTheme.colors.warningBackground};
`
